import { CustomButton } from "@/components/ui/Buttons";
import LeftArrowShort from "@/assets/icons/authentication/LeftArrowShort.svg";

import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import AuthInput from "../../AuthInput";
import AuthDropdown, { AuthDropdownOption } from "../../AuthDropdown";
import {
  prevStep,
  nextStep,
  updateField,
  updateV2Field,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";

import {
  COUNTRIES,
  INDIAN_STATES,
  MAJOR_CITIES,
  MAJOR_CITIES_BY_COUNTRY,
  MAJOR_CITIES_BY_STATE,
  STATES_BY_COUNTRY,
} from "@/constant";
import AuthPhoneInput from "../../AuthPhoneInput";
import {
  scrollToFirstError,
  validateRequiredFields,
} from "@/utils/formValidationV2";

type Step3SignUpProps = {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const ApplicationStep3 = ({
  currentStep,
  setCurrentStep,
}: Step3SignUpProps) => {
  const dispatch = useAppDispatch();

  const [errors, setErrors] = useState<Record<string, string>>({});

  // validation handler
  const handleSubmit = () => {
    const formValues = {
      phoneNumber,
      pincode,
      country: countryValue,
      state: stateValue,
      city: cityValue,
      address,
    };

    const validationErrors = validateRequiredFields(formValues, [
      "phoneNumber",
      "pincode",
      "country",
      "state",
      "city",
      "address",
    ]);

    // Optional custom validations
    if (phoneNumber && phoneNumber.length < 8) {
      validationErrors.phoneNumber = "Enter a valid phone number";
    }

    if (pincode && pincode.length < 4) {
      validationErrors.pincode = "Enter a valid pincode";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToFirstError(validationErrors);
      return;
    }

    setErrors({});
    dispatch(nextStep());
    setCurrentStep((prev) => prev + 1);
  };

  // Clear Function
  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  // -------------------- selectors --------------------
  const phoneNumber = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.phoneNumber,
  );

  const pincode = useAppSelector((s) => s.signUpV2.v2Extras.pincode);

  // IMPORTANT: we will store country/state/city as DISPLAY TEXT (e.g. "India")
  const countryValue = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.country,
  );
  const stateValue = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.state,
  );
  const cityValue = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.city,
  );

  const address = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.address,
  );
  const mapLink = useAppSelector((s) => s.signUpV2.v2Extras.mapLink);

  console.log({ phoneNumber, countryValue, stateValue, address, mapLink });

  // -------------------- dropdown options --------------------
  const [countryOptions, setCountryOptions] = useState<AuthDropdownOption[]>(
    () => COUNTRIES.map((c) => ({ key: c.title, label: c.title })),
  );

  const [stateOptions, setStateOptions] = useState<AuthDropdownOption[]>([]);

  const [cityOptions, setCityOptions] = useState<AuthDropdownOption[]>([]);

  // selected option objects
  // Match by key (legacy) or label — custom "others" options use an internal key but we persist the label
  const matchByKeyOrLabel = (o: AuthDropdownOption, stored: string) =>
    String(o.key) === String(stored) || String(o.label) === String(stored);

  const selectedCountry =
    countryOptions.find((o) => matchByKeyOrLabel(o, String(countryValue))) ??
    null;

  const selectedState =
    stateOptions.find((o) => matchByKeyOrLabel(o, String(stateValue))) ?? null;

  const selectedCity =
    cityOptions.find((o) => matchByKeyOrLabel(o, String(cityValue))) ?? null;

  // Recompute state options when country changes
  useEffect(() => {
    const states =
      STATES_BY_COUNTRY[countryValue] ||
      (countryValue === "India" ? INDIAN_STATES : []);
    setStateOptions(
      (states || []).map((s) => ({ key: s.title, label: s.title })),
    );
  }, [countryValue]);

  // Recompute city options when country or state changes
  useEffect(() => {
    const byState = MAJOR_CITIES_BY_STATE?.[countryValue]?.[stateValue];
    if (byState?.length) {
      setCityOptions(byState.map((c) => ({ key: c.title, label: c.title })));
      return;
    }
    const byCountry = MAJOR_CITIES_BY_COUNTRY?.[countryValue];
    if (byCountry?.length) {
      setCityOptions(byCountry.map((c) => ({ key: c.title, label: c.title })));
      return;
    }
    setCityOptions(
      (MAJOR_CITIES || []).map((c) => ({ key: c.title, label: c.title })),
    );
  }, [countryValue, stateValue]);

  // -------------------- handlers (reset dependencies) --------------------
  const handleCountryChange = (opt: AuthDropdownOption) => {
    const nextCountry = String(opt.label);

    dispatch(updateField("country", nextCountry));
    dispatch(updateField("state", ""));
    dispatch(updateField("city", ""));

    clearFieldError("country");
    clearFieldError("state");
    clearFieldError("city");
  };

  const handleStateChange = (opt: AuthDropdownOption) => {
    const nextState = String(opt.label);

    dispatch(updateField("state", nextState));
    dispatch(updateField("city", ""));

    clearFieldError("state");
    clearFieldError("city");
  };

  const handleCityChange = (opt: AuthDropdownOption) => {
    dispatch(updateField("city", String(opt.label)));
    clearFieldError("city");
  };
  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-x-2">
          <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
            <p className="text-white text-[11px] leading-5">Step 3 of 5</p>
          </div>
          <h1 className="text-[#060B13] text-xl font-medium">
            Contact Information
          </h1>
        </div>
        <p className="text-[#475467] text-xs mt-2">
          We use this to communicate about projects and payments.
        </p>
      </div>

      <div className="space-y-5">
        {/* Phone */}
        <div id="phoneNumber">
          <AuthPhoneInput
            name="phone"
            label="Phone number"
            value={phoneNumber}
            onChange={(val: string) => {
              dispatch(updateField("phoneNumber", val));
              clearFieldError("phoneNumber");
            }}
          />
          {errors.phoneNumber && (
            <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
          )}
        </div>

        {/* Pincode */}
        <div id="pincode">
          <AuthInput
            label="Postal Code"
            type="text"
            name="pincode"
            placeholder="e.g. SW1A 1AA"
            autoComplete="postal-code"
            value={pincode}
            onChange={(e) => {
              dispatch(updateV2Field("pincode", e.target.value));
              clearFieldError("pincode");
            }}
            error={errors.pincode}
          />
        </div>

        {/* Country */}
        <div id="country">
          <AuthDropdown
            label="Country"
            options={countryOptions}
            value={selectedCountry}
            onChange={handleCountryChange}
            onOptionsChange={setCountryOptions}
            error={errors.country}
            others
            searchable
          />
        </div>

        {/* State + City */}
        <div className="flex items-start flex-col md:flex-row gap-5 lg:gap-x-[26px]">
          <div className="w-full" id="state">
            <AuthDropdown
              label="State"
              options={stateOptions}
              value={selectedState}
              onChange={handleStateChange}
              onOptionsChange={setStateOptions} // ← add
              error={errors.state}
              others
            />
          </div>

          <div className="w-full" id="city">
            <AuthDropdown
              label="City"
              options={cityOptions}
              value={selectedCity}
              onChange={handleCityChange}
              onOptionsChange={setCityOptions} // ← add
              error={errors.city}
              others
            />
          </div>
        </div>

        {/* Address */}
        <div id="address">
          <AuthInput
            label="Detailed Address"
            name="address"
            placeholder="123 Main St, Anytown"
            value={address}
            onChange={(e) => {
              dispatch(updateField("address", e.target.value));
              clearFieldError("address");
            }}
            error={errors.address}
          />
        </div>

        {/* Map Link (Optional) */}
        <AuthInput
          label="Google Map Link (Optional)"
          name="mapLink"
          placeholder="Paste Google map link"
          value={mapLink}
          onChange={(e) => dispatch(updateV2Field("mapLink", e.target.value))}
        />
      </div>

      {/* Buttons */}
      <div className="mt-[50px] flex items-center justify-end gap-3">
        <CustomButton
          variant="outline"
          border="1px solid #E4E4E4"
          fontSize="14px"
          fontWeight="600"
          color="#4D4D4D"
          padding="5px 10.5px"
          borderRadius="200px"
          className="flex items-center gap-x-1.5"
          onClick={() => {
            dispatch(prevStep());
            setCurrentStep((prev) => prev - 1);
          }}
        >
          <LeftArrowShort />
          Go Back
        </CustomButton>

        <CustomButton
          backgroundColor="#FF6600"
          fontSize="14px"
          fontWeight="600"
          color="white"
          padding="5px 10.5px"
          borderRadius="200px"
          onClick={handleSubmit}
        >
          Proceed
        </CustomButton>
      </div>
    </div>
  );
};

export default ApplicationStep3;
